import { useState } from 'react';
import { Loader2, Download } from 'lucide-react';
import { Button } from '@/components/ui';
import type { ProgramCertificateMetadata } from '@court-sync/shared';
import { downloadBlob, generateProgramCertificatePdfBlob } from '@/utils/certificateGenerator';

export interface CertificateDownloadButtonProps {
  /**
   * Build PDF metadata (after any server steps). Use this for flows that must
   * record certificate issuance before generating the PDF.
   */
  loadCertificate: () => Promise<{ meta: ProgramCertificateMetadata; filenameBase: string }>;
  onAfterDownload?: () => void | Promise<void>;
  variant?: 'default' | 'outline' | 'secondary';
  size?: 'default' | 'sm';
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  onError?: (error: unknown) => void;
}

export function CertificateDownloadButton({
  loadCertificate,
  onAfterDownload,
  variant = 'default',
  size = 'sm',
  className,
  children,
  disabled = false,
  onError,
}: CertificateDownloadButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const { meta, filenameBase } = await loadCertificate();
      const blob = await generateProgramCertificatePdfBlob(meta);
      const safe = filenameBase.replace(/[^a-zA-Z0-9-_]+/g, '-');
      downloadBlob(blob, `${safe}.pdf`);
      await onAfterDownload?.();
    } catch (e) {
      onError?.(e);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      disabled={disabled || loading}
      onClick={() => void handleClick()}
    >
      {loading ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <Download className="mr-2 h-4 w-4" aria-hidden />
      )}
      {children ?? 'Download PDF'}
    </Button>
  );
}
